import loadable from '@loadable/component';
import {
    root,
    IAppConfig,
    ICachedComponentsRegistry,
    IComponentRegistry,
    IComponentViewController,
    IManifestServiceRegistryComp,
    IServiceRegistryComponent,
    RegisteredComponent,
    ServiceInitializer
} from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ServiceBusController } from './ServiceBusController';
import { getServices } from './utils';

root.ServiceRegistry = root.ServiceRegistry || {};

export abstract class Service implements IServiceRegistryComponent {
    protected logger: ELCLogger;

    public serviceBusController: ServiceBusController;
    public componentsRegistry: IComponentRegistry = {};
    public cachedComponentsRegistry: ICachedComponentsRegistry = {};
    public isLazy: boolean = false;
    public isStarted: boolean = false;
    public views: string[] = [];
    public startPromise: Promise<void>;

    constructor(
        public name: string,
        public version: string,
        public serviceInitializer: ServiceInitializer
    ) {}

    public async initializeService(appConfig: IAppConfig): Promise<void> {
        try {
            await this.serviceInitializer(appConfig);
            this.isStarted = true;
        } catch (error) {
            this.logger.error({
                message: `Could not start service ${this.name}`,
                triggerType: TriggerType.render,
                payload: {
                    error,
                    context: {
                        serviceRegistry: root.ServiceRegistry
                    }
                }
            });
        }
    }

    public start(appConfig: IAppConfig): Promise<void> {
        if (!this.startPromise && !this.isStarted) {
            this.logger = new ELCLogger({
                serviceName: this.name,
                environment: appConfig.env || root.env,
                buid: appConfig.businessUnitId || root.buid,
                serviceVersion: this.version
            });

            this.startPromise = this.initializeService(appConfig);
        }

        return this.startPromise as Promise<void>;
    }

    public register = (overrideValues?: Omit<IManifestServiceRegistryComp, 'start'>) => {
        root.ServiceRegistry = getServices();

        root.ServiceRegistry[this.name] = Object.assign(
            root.ServiceRegistry[this.name] || {},
            {
                start: this.start,
                startPromise: this.startPromise,
                name: this.name,
                version: this.version,
                views: this.views,
                getView: this.getView,
                registerView: this.registerView,
                initializeService: this.initializeService,
                setViewInCache: this.setViewInCache,
                getViewFromCache: this.getViewFromCache,
                serviceInitializer: this.serviceInitializer,
                componentsRegistry: this.componentsRegistry,
                cachedComponentsRegistry: this.cachedComponentsRegistry,
                isStarted: this.isStarted,
                isLazy: this.isLazy
            },
            overrideValues || {}
        );
    };

    public getView = (componentName: string) => {
        return this.componentsRegistry[componentName] || null;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setViewInCache = (componentName: string, component: React.ComponentType<any>) => {
        this.cachedComponentsRegistry[componentName] = component;
    };

    public getViewFromCache = (componentName: string) => {
        return this.cachedComponentsRegistry[componentName] || null;
    };

    public registerView = (
        componentName: string,
        component: RegisteredComponent,
        viewController?: () => IComponentViewController
    ) => {
        this.views.push(componentName);

        this.componentsRegistry[componentName] = {
            componentName,
            component: loadable(component),
            viewController
        };
    };
}
